import type {
  CSSProperties,
  KeyboardEvent,
  ReactElement,
  ReactNode,
  Ref
} from 'react';
import {
  cloneElement,
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import styled, { css } from 'styled-components';
import {
  createReadableClass,
  invariant,
  isMobile
} from '@elfsight/widget-utils';
import useMergedRef from '@react-hook/merged-ref';
import { Popout } from '../popout';
import { composeEventHandler } from '../utils';

export type TooltipProps = {
  children: ReactElement & { ref?: Ref<Element> };
  title?: ReactNode;
  background?: string;
  fontColor?: string;
  borderRadius?: number;
  withoutTransition?: boolean;
  disabled?: boolean;
  maxWidth?: number;
  style?: CSSProperties;
  offset?: number;
};

export const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(
  (
    {
      children,
      title,
      background = 'rgb(17, 17, 17)',
      fontColor = 'rgb(255, 255, 255)',
      borderRadius = 4,
      disabled = false,
      offset = 4,
      withoutTransition = false,
      maxWidth,
      style
    },
    forwardingRef
  ) => {
    invariant(
      isValidElement(children),
      'Expected the children to be a valid react element.'
    );

    const [show, setShow] = useState(false);
    const [disableTransition, setDisableTransition] =
      useState(withoutTransition);

    // freeze opening feature allows to ignore opening
    // after interaction with an anchor until it loses a focus
    const freezeOpeningRef = useRef(false);

    // create close/open schedule timers refs
    const openingTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);
    const closingTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);

    // clean up timers when component unmounts
    useEffect(() => {
      if (openingTimerRef.current) {
        clearTimeout(openingTimerRef.current);
        openingTimerRef.current = undefined;
      }

      if (closingTimerRef.current) {
        clearTimeout(closingTimerRef.current);
        closingTimerRef.current = undefined;
      }
    }, []);

    // the functions schedules opening
    const scheduleOpening = () => {
      // unschedule closing
      if (closingTimerRef.current) {
        clearTimeout(closingTimerRef.current);
        closingTimerRef.current = undefined;
      }

      // ignore scheduling if it's open or opening is already scheduled or frozen
      if (show || openingTimerRef.current || freezeOpeningRef.current) {
        return;
      }

      // schedule opening
      openingTimerRef.current = setTimeout(() => {
        setShow(true);
        openingTimerRef.current = undefined;
      }, 200);
    };

    // the function schedule closing
    const scheduleClosing = () => {
      // unschedule opening
      if (openingTimerRef.current) {
        clearTimeout(openingTimerRef.current);
        openingTimerRef.current = undefined;
      }

      // ignore scheduling if it's close or closing is already scheduled
      if (!show || closingTimerRef.current) {
        return;
      }

      // schedule closing
      closingTimerRef.current = setTimeout(() => {
        setShow(false);
        closingTimerRef.current = undefined;
      }, 200);
    };

    // the functions closes the Tooltip immediately and freezes opening
    const closeImmediate = () => {
      setShow(false);
      setDisableTransition(true);
      freezeOpeningRef.current = true;
    };

    useEffect(() => {
      const handleScroll = () => setShow(false);

      window.addEventListener('scroll', handleScroll, {
        capture: true
      });

      return () =>
        window.removeEventListener('scroll', handleScroll, {
          capture: true
        });
    }, []);

    // close tooltip when page is hidden
    useEffect(() => {
      const handleVisibilityChange = () => {
        if (document.hidden) {
          setShow(false);
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () =>
        document.removeEventListener(
          'visibilitychange',
          handleVisibilityChange
        );
    }, []);

    useEffect(() => {
      setDisableTransition(withoutTransition);
    }, [withoutTransition]);

    // get child element props
    const { props: childrenOriginalProps, ref: childrenOriginalRef } = children;

    // in order to arrange the Tooltip to the right position
    // we need an anchor element
    const [anchorElement, setAnchorElement] = useState<Element | null>(null);

    // this callback ref sets the anchor element and forward a ref
    // to the original child element
    const childrenRef = useCallback((nextAnchorElement: Element | null) => {
      setAnchorElement(nextAnchorElement);
    }, []);

    const handleChildrenMouseOver = composeEventHandler(
      scheduleOpening,
      childrenOriginalProps.onMouseOver
    );

    const handleChildrenClick = composeEventHandler(
      scheduleOpening,
      childrenOriginalProps.onClick
    );

    const handleChildrenMouseLeave = composeEventHandler(() => {
      scheduleClosing();
      freezeOpeningRef.current = false;
    }, childrenOriginalProps.onMouseLeave);

    const handleChildrenMouseDown = composeEventHandler(
      closeImmediate,
      childrenOriginalProps.onMouseDown
    );

    const handleChildrenFocus = composeEventHandler(
      scheduleOpening,
      childrenOriginalProps.onFocus
    );

    const handleChildrenBlur = composeEventHandler(() => {
      scheduleClosing();
      freezeOpeningRef.current = false;
    }, childrenOriginalProps.onBlur);

    const handleChildrenKeyDown = composeEventHandler(
      (event: KeyboardEvent) => {
        if (event.key === 'Escape' || event.key === 'Enter') {
          closeImmediate();
        }
      },
      childrenOriginalProps.onKeyDown
    );

    // save children's ref
    const mergedRef = useMergedRef(childrenRef, childrenOriginalRef || null);

    const mobileHandlers = {
      onClick: handleChildrenClick
    };
    const desktopHandlers = {
      onMouseOver: handleChildrenMouseOver,
      onMouseDown: handleChildrenMouseDown
    };

    const finalChildrenProps = {
      ...(isMobile() ? mobileHandlers : desktopHandlers),
      ref: mergedRef,
      onMouseLeave: handleChildrenMouseLeave,
      onFocus: handleChildrenFocus,
      onBlur: handleChildrenBlur,
      onKeyDown: handleChildrenKeyDown
    };

    return (
      <>
        {cloneElement(children, finalChildrenProps)}

        {!disabled && anchorElement && (
          <Popout
            ref={forwardingRef}
            show={show}
            anchorElement={anchorElement}
            direction="upward"
            alignment="center"
            offsetTop={offset}
            withoutTransition={disableTransition}
          >
            <TooltipContainer
              $maxWidth={maxWidth}
              $background={background}
              $color={fontColor}
              $borderRadius={borderRadius}
              style={style}
              onMouseOver={scheduleOpening}
              onMouseLeave={scheduleClosing}
            >
              {title}
            </TooltipContainer>
          </Popout>
        )}
      </>
    );
  }
);

export const TooltipContainer = styled.div.attrs({
  className: createReadableClass({ component: 'tooltip', element: 'container' })
})<{
  $maxWidth?: number;
  $background: string;
  $color: string;
  $borderRadius: number;
}>`
  ${({ $maxWidth = 300, $background, $color, $borderRadius }) => css`
    max-width: ${$maxWidth}px;
    background: ${$background};
    color: ${$color};
    border-radius: ${$borderRadius}px;
    padding: 6px 8px;
    box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.3);
    text-align: center;
    font-size: 13px;
    line-height: 1.33;
    border-radius: 4px;
  `}
`;
